// Implements: .claude/skills/rehaut-design/ui_kits/rehaut-pacientes/primitives.jsx
// See: .claude/skills/rehaut-design/HANDOFF.md
//
// Date helpers for the v2 design system. The kit anchors to a fixed
// TODAY string for stable mockup output; production reads the actual
// current date in the user's local zone (CDMX-aware via the existing
// `localToday` upstream when needed).

const MONTHS_ES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

/** Parse a YYYY-MM-DD or ISO date and return Date at UTC midnight (so
 *  daysBetween math doesn't drift across DST). Returns null on invalid. */
function parseISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(d.getTime()) ? null : d;
}

// ── Clinical-date rule (Bianca fix, 2026-07) ─────────────────
// notas/tickets timestamps mix two populations: MIGRATED rows stored at
// exactly 00:00:00 UTC (date-only imports — their UTC calendar date IS
// the intended date) and ORGANIC rows with real timestamps (an evening
// event is already "tomorrow" in UTC; its clinic-local CDMX date is the
// truth). DB-validated 2026-07-04: the populations are perfectly
// separated (zero organic midnight rows; real timestamps began
// 2026-03-09). Rule: exact-midnight-UTC → UTC date, else → CDMX date.
// Mexico abolished DST, so CDMX is a fixed UTC-6 — the Intl formatters
// below are stable year-round.
// Known residual risk: an organic event stamped at exactly
// 00:00:00.000 UTC would be misclassified as migrated — probability
// ~1e-8 per row, accepted.

const CDMX_KEY_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' });

function isMidnightUtc(d: Date): boolean {
  return d.getUTCHours() === 0 && d.getUTCMinutes() === 0
    && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
}

/** Today's clinical (CDMX) calendar day as YYYY-MM-DD. */
function cdmxToday(): string {
  return CDMX_KEY_FMT.format(new Date());
}

/** The clinical calendar day of a timestamp or date string, as
 *  YYYY-MM-DD — the single source of truth for grouping, bucketing and
 *  display. Midnight-UTC rows (migrated date-only) keep their UTC date;
 *  everything else renders in clinic time. Date-only strings parse to
 *  midnight UTC and so keep their literal date. Null on invalid. */
export function clinicalDateKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (isMidnightUtc(d)) {
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${d.getUTCFullYear()}-${m}-${day}`;
  }
  return CDMX_KEY_FMT.format(d); // en-CA renders YYYY-MM-DD
}

/** "21 may 26" — short Spanish date on the CLINICAL day. Use this for
 *  every timestamptz (created_at etc.); it derives from clinicalDateKey
 *  so display can never disagree with grouping. */
export function formatClinicalDate(iso: string | null | undefined): string {
  const key = clinicalDateKey(iso);
  if (!key) return '—';
  const [y, m, d] = key.split('-').map(Number);
  return `${d} ${MONTHS_ES[m - 1]} ${String(y).slice(2)}`;
}

/** "21 may 2026" — formatClinicalDate with a 4-digit year, for a lone
 *  date field where "21 may 26" reads as two day numbers (ticket drawer
 *  FECHA, iPad #84). Same clinical-day rule. */
export function formatClinicalDateLong(iso: string | null | undefined): string {
  const key = clinicalDateKey(iso);
  if (!key) return '—';
  const [y, m, d] = key.split('-').map(Number);
  return `${d} ${MONTHS_ES[m - 1]} ${y}`;
}

/** Calendar-day delta from `a` to `b`. Positive when b is after a.
 *  Operands are bucketed on CLINICAL days (see clinicalDateKey), so a
 *  timestamptz created 18:00-24:00 CDMX counts on its clinic day, not
 *  the UTC next-day. Date-only strings behave exactly as before. */
export function daysBetween(a: string, b: string): number {
  const da = parseISODate(clinicalDateKey(a) ?? a);
  const db = parseISODate(clinicalDateKey(b) ?? b);
  if (!da || !db) return 0;
  return Math.round((db.getTime() - da.getTime()) / 86400000);
}

/** Spanish relative phrasing keyed to today (caller-provided so tests
 *  can pin a TODAY without monkeying the system clock). Symmetric:
 *  past → "hace 4 d", future → "en 4 d", same day → "hoy". Uses the
 *  d / sem / mes(es) / año(s) bucket ladder for general-purpose use (notas
 *  timeline, last-visit). Use `relativeDateSigned` instead when you
 *  need the kit's tighter sem-heavy buckets (`<60d` → sem) for
 *  short-term tracking like overdue invoices. */
export function relativeDate(iso: string | null | undefined, today?: string): string {
  if (!iso) return '—';
  const ref = today || cdmxToday();
  const d = daysBetween(iso, ref);
  if (d === 0) return 'hoy';
  if (d === 1) return 'ayer';
  if (d === -1) return 'mañana';
  const past = d > 0;
  const abs = Math.abs(d);
  const prefix = past ? 'hace' : 'en';
  if (abs < 7) return `${prefix} ${abs} d`;
  if (abs < 30) return `${prefix} ${Math.floor(abs / 7)} sem`;
  if (abs < 365) return `${prefix} ${monthsEs(Math.floor(abs / 30))}`;
  return `${prefix} ${yearsEs(Math.floor(abs / 365))}`;
}

/** Mirror of the kit's `relativeDateSigned` from
 *  `_shared/primitives.jsx`. Same shape as `relativeDate` but with
 *  sem buckets up to 60d (vs 30d), no año bucket — better fit for
 *  short-term scheduling surfaces (vencimientos, citas). */
export function relativeDateSigned(iso: string | null | undefined, today?: string): string {
  if (!iso) return '—';
  const ref = today || cdmxToday();
  // Kit signs the delta the other way (positive = future) — mirror it.
  const d = -daysBetween(iso, ref);
  if (d === 0) return 'hoy';
  if (d === 1) return 'mañana';
  if (d === -1) return 'ayer';
  if (d > 0 && d < 7) return `en ${d} d`;
  if (d < 0 && d > -7) return `hace ${-d} d`;
  if (d > 0 && d < 60) return `en ${Math.floor(d / 7)} sem`;
  if (d < 0 && d > -60) return `hace ${Math.floor(-d / 7)} sem`;
  if (d > 0) return `en ${monthsEs(Math.floor(d / 30))}`;
  return `hace ${monthsEs(Math.floor(-d / 30))}`;
}

/** Hour-aware "how long ago" for message-age surfaces (inbox triage,
 *  Front, contactos): "ahora" / "hace 12 min" / "hace 3 h" / "ayer" /
 *  "hace 2 d" / "hace 5 sem" / "hace 3 meses" / "hace 1 año". The one
 *  ladder those rows share, so a plural can only be wrong in one place. */
export function relativeFromHours(hours: number | null | undefined): string {
  if (hours == null || !Number.isFinite(hours)) return '';
  if (hours < 1) {
    const m = Math.round(hours * 60);
    return m <= 0 ? 'ahora' : `hace ${m} min`;
  }
  if (hours < 24) return `hace ${Math.round(hours)} h`;
  const d = Math.round(hours / 24);
  if (d === 1) return 'ayer';
  if (d < 7) return `hace ${d} d`;
  if (d < 30) return `hace ${Math.floor(d / 7)} sem`;
  if (d < 365) return `hace ${monthsEs(Math.floor(d / 30))}`;
  return `hace ${yearsEs(Math.floor(d / 365))}`;
}

/** "1 mes" / "3 meses" — the plural every relative-time ladder needs. */
export function monthsEs(n: number): string {
  return `${n} ${n === 1 ? 'mes' : 'meses'}`;
}

/** "1 año" / "2 años" — spelled out; the bare "a" read as a typo. */
export function yearsEs(n: number): string {
  return `${n} ${n === 1 ? 'año' : 'años'}`;
}

/** "12 abr 26" — short Spanish date. Useful in the timeline column. */
export function formatDate(iso: string | null | undefined): string {
  const d = parseISODate(iso);
  if (!d) return '—';
  return `${d.getUTCDate()} ${MONTHS_ES[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
}

/** "$1,200" — Mexican peso, no decimal places. Money sign per brand. */
export function formatMoney(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return '$' + Math.round(n).toLocaleString('es-MX');
}

/** Age in completed years from a YYYY-MM-DD birthdate. Null on invalid. */
export function age(dob: string | null | undefined, today?: string): number | null {
  if (!dob) return null;
  const ref = today || cdmxToday();
  const days = daysBetween(dob, ref);
  if (days < 0) return null;
  return Math.floor(days / 365.25);
}

// ── CDMX-aware formatting (FLAGSHIPS) ────────────────────────
// formatDate above truncates to the UTC calendar day — fine for
// date-only strings, wrong for timestamptz (a 19:00 CDMX event shows
// tomorrow's date). For timestamptz values use `formatClinicalDate`
// above — it follows the clinical-date rule so display can never
// disagree with grouping.

const CDMX_MONTHYEAR_FMT = new Intl.DateTimeFormat('es-MX', {
  timeZone: 'America/Mexico_City', month: 'long', year: 'numeric',
});
const UTC_MONTHYEAR_FMT = new Intl.DateTimeFormat('es-MX', {
  timeZone: 'UTC', month: 'long', year: 'numeric',
});

/** "marzo de 2024" on the CLINICAL day — masthead "Paciente desde".
 *  Follows the clinical-date rule: exact-midnight-UTC rows are migrated
 *  date-only records whose UTC month IS the intended month (a CDMX render
 *  would pull them one month back at month boundaries); everything else
 *  renders in clinic time. */
export function monthYearCdmx(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return (isMidnightUtc(d) ? UTC_MONTHYEAR_FMT : CDMX_MONTHYEAR_FMT).format(d);
}

/** "4 de julio" — today's eyebrow label in clinic time. */
export function todayEyebrowMx(): string {
  return new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', timeZone: 'America/Mexico_City' });
}
